import React, {
    useMemo, useEffect, useCallback
} from 'react';
import ColumnSelector from '../../../webcommon/columnselector/ColumnSelector';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import classnames from 'classnames';
import moment from 'moment';
import fp from 'lodash/fp';

import { getFilters } from '../../../webcommon/header/filterheader/selectors';
import Grid from '../../../webcommon/grid/Grid';
import FilterHeader from '../../../webcommon/header/filterheader/FilterHeader';
import Checkbox from '../../../webcommon/common/checkbox/Checkbox';

import GridHeader from '../../../common/gridheader/GridHeader';
import ToggleSwitch from '../../../common/gridheader/toggleswitch/ToggleSwitch';
import styles from './ImImpactSummary.module.scss';
import gridHeaderStyles from './GridHeaderOverride.module.scss';
import gridStyles from './GridOverride.module.scss';
import {
    combinedColumnsSelector, getEodDate, getRows, getCurrencyToShow,
    getLoading, getActiveCsa, getTrades, getOriginalTrades, getShowAllRows,
    getRowsToExpand
} from './selectors';
import { getSummaryImpactRows } from '../../impactAnalysisIdSummary/selectors';
import { getCsas } from '../../../app/selectors';
import {
    csvExportEmpty, setImpactSummaryColumns,
    imImpactSummaryLoaded, compareCsasRequest, initialise, setShowAllRows, expandRows
} from './actions';
import { setCurrencyToShow } from '../../summary/actions';
import ExportAsCsvButton from '../../../common/gridheader/exportbutton/ExportAsCsvButton';
import RecalculateData from '../../../common/gridheader/recalculatedata/RecalculateData';
import { submitBookingTrades } from '../tradepricedtoday/actions';
import { tradesSelectedAreDifferent } from '../../../common/helpermethods/HelperMethods';
import FilterStyles from './FilterHeaderOverride.module.scss';
import { mapCsaSummariesToGrid, mapCsaSummariesToGridFiltered } from '../../../api/adapters/csaImpactAdapter';
import { typeOrderingValues } from './constants';
import * as appConstants from '../../../common/constants';

const calculateCsvFilename = date => `IM_Impact_Summary_${(date ? moment(date) : moment()).format(appConstants.dateFormat)}.csv`;

const getRowLevelClassName = (rowInfo) => {
    if (rowInfo.original.titleRow) { return '-headerRow'; }
    return '-noBackground';
};

const setImpactColumns = (initialColumns, columns) => {
    const data = initialColumns.filter(x => x.title.length === 0)
        .concat(columns.map((column) => {
            const { id, visible } = column;
            return { id, visible };
        }));
    return data;
};

const processDataForCsvExport = (rows) => {
    const mapKeys = (row) => {
        const {
            title, trades, imAmountEur, imAmountConfirmedEur, imAmountIncrementEur,
            imTotalEur, imAmountUsd, imAmountConfirmedUsd, imAmountIncrementUsd,
            imTotalUsd, imThresholdCollectUsd, imThresholdCollectEur,
            imThresholdPostUsd, imThresholdPostEur
        } = row;
        return {
            Title: title,
            'Number of Trades': trades,
            'IM Amount EUR': imAmountEur,
            'IM Confirmed EUR': imAmountConfirmedEur,
            'IM Increment EUR': imAmountIncrementEur,
            'IM Total EUR': imTotalEur,
            'IM Amount USD': imAmountUsd,
            'IM Confirmed USD': imAmountConfirmedUsd,
            'IM Increment USD': imAmountIncrementUsd,
            'IM Total USD': imTotalUsd,
            'IM Threshold Collect USD': imThresholdCollectUsd,
            'IM Threshold Collect EUR': imThresholdCollectEur,
            'IM Threshold Post USD': imThresholdPostUsd,
            'IM Threshold Post EUR': imThresholdPostEur
        };
    };
    return rows.map(mapKeys);
};

const ImImpactSummary = (props) => {
    const {
        imImpactSummaryLoaded,
        initialise,
        columns,
        submitBookingTrades,
        trades,
        readOnly,
        summaryRows,
        rows,
        allCsas,
        expandedRows,
        activeCsa,
        activeCsaReadOnly,
        showAllRows
    } = props;

    useEffect(() => {
        imImpactSummaryLoaded();
        initialise();
    }, [imImpactSummaryLoaded, initialise]);

    const memoizedColumns = useMemo(() => columns
        .filter(column => column.visible), [columns]);

    const changes = tradesSelectedAreDifferent(props.trades, props.originalTrades);

    const recalculateData = useCallback(() => {
        submitBookingTrades(trades);
    }, [trades, submitBookingTrades]);

    const toggleSwitch = () => (
        props.currencyToShow === 'EUR' ? props.setCurrencyToShow('USD') : props.setCurrencyToShow('EUR')
    );

    const impactRows = useMemo(() => (readOnly ? summaryRows : rows), [readOnly, rows, summaryRows]);

    const rowsWithCounterparties = useMemo(() => impactRows.map((row) => {
        const counterpartyObj = fp.find(csa => csa.value === row.csa)(allCsas);
        const counterparty = fp.get('counterparty')(counterpartyObj);
        return fp.set('counterparty', counterparty)(row);
    }), [allCsas, impactRows]);

    const setSelected = useCallback(rows => rows.map((row) => {
        if (row.title || row.csa === activeCsa) {
            return fp.set('selected', true)(row);
        }
        return fp.set('selected', false)(row);
    }), [activeCsa]);

    const restructureRows = useCallback(rows => rows.reduce(
        (acc, cur) => {
            const activeCsaVal = readOnly ? activeCsaReadOnly : activeCsa;
            const selectedCsaTitle = cur.title === activeCsaVal;
            const selectedCsaRow = cur.csa === activeCsaVal;
            const possibleParent = fp.find(a => cur.type === a.title)(acc);
            if (selectedCsaTitle) {
                return (
                    [...acc,
                        {
                            title: 'Selected',
                            type: cur.type,
                            children: [cur.id],
                            typeRow: true,
                            selected: true
                        },
                        fp.set('selected', true)(cur)]
                );
            }
            if (selectedCsaRow) {
                const selectedParent = fp.find(a => a.title === 'Selected')(acc);
                const children = fp.get('children')(selectedParent);
                children.push(cur.id);
                fp.set('children', children)(selectedParent);
                return (
                    [...acc,
                        fp.set('selected', true)(cur)]);
            }
            if (possibleParent) {
                const children = fp.get('children')(possibleParent);
                children.push(cur.id);
                fp.set('children', children)(possibleParent);
                return (
                    [...acc,
                        cur]);
            }
            return (
                [...acc,
                    { title: cur.type, children: [cur.id], typeRow: true },
                    cur]
            );
        }, []
    ), [readOnly, activeCsaReadOnly, activeCsa]);

    const typeValue = type => (type ? typeOrderingValues[type] : 0);

    const customSort = useCallback((a, b) => {
        const valA = typeValue(a.csaType);
        const valB = typeValue(b.csaType);
        if (a.selected < b.selected) { return 1; }
        if (a.selected > b.selected) { return -1; }
        if (valA < valB) { return 1; }
        if (valA > valB) { return -1; }
        return 0;
    }, []);

    const expandRows = useCallback(rows => rows.map((row) => {
        if (row.selected) {
            const isTrue = fp.get('Selected')(expandedRows);
            const newRow = isTrue
                ? fp.set('expanded', true)(row)
                : fp.set('expanded', false)(row);
            return newRow;
        }
        const type = row.typeRow ? row.title : row.type;
        const isTrue = fp.get(`${type}`)(expandedRows);
        const newRow = isTrue
            ? fp.set('expanded', true)(row)
            : fp.set('expanded', false)(row);
        return newRow;
    }), [expandedRows]);

    const showRows = useCallback(rows => (showAllRows
        ? mapCsaSummariesToGrid(rows)
        : mapCsaSummariesToGridFiltered(rows)), [showAllRows]);

    const filterFunction = (row) => {
        if (!row.typeRow) {
            return row.expanded ? row : null;
        }
        return row;
    };

    const memoizedRows = useMemo(() => {
        const rowsWithSelected = setSelected(rowsWithCounterparties);
        const sortedRows = rowsWithSelected.sort(customSort);
        const filterRows = fp.flow(
            showRows,
            restructureRows,
            expandRows,
            fp.filter(row => filterFunction(row))
        )(sortedRows);
        return filterRows;
    }, [customSort, expandRows, restructureRows, rowsWithCounterparties, setSelected, showRows]);

    const gridTitleWrapper = useMemo(() => classnames({
        [styles.gridTitleWrapper]: true,
        [styles.noDisplay]: readOnly
    }), [readOnly]);

    const toggleSwitchStyle = useMemo(() => classnames({
        [styles.toggleSwitch]: true,
        [styles.noDisplay]: !readOnly
    }), [readOnly]);

    return (
        <div className={ styles.iMImpactSummary }>
            <GridHeader changesInTradeSelection={ changes } styles={ gridHeaderStyles } title="IM Impact Summary">
                {changes ? (
                    <>
                        <RecalculateData recalculatePageData={ recalculateData } />
                        <div className={ styles.separator }>&nbsp;</div>
                    </>
                ) : null}
                <div className={ gridTitleWrapper }>
                    <div className={ styles.gridHeaderTitle }>
                        <FilterHeader fixed id="ImImpactSummary" styles={ FilterStyles } />
                    </div>
                </div>
                <div className={ toggleSwitchStyle }>
                    <ToggleSwitch
                        optionLeft="EUR"
                        optionRight="USD"
                        toggleIsLeft={ props.currencyToShow === 'EUR' }
                        toggleSwitch={ toggleSwitch }
                    />
                </div>
                <div className={ styles.separator }>&nbsp;</div>
                <div className={ styles.showRows }> Show all results
                    <div className={ styles.checkboxWrapper }>
                        <Checkbox
                            checked={ props.showAllRows }
                            onChange={ props.setShowAllRows }
                        />
                    </div>
                </div>
                <div className={ styles.separator }>&nbsp;</div>
                <div className={ styles.columnSelectorRelative }>
                    <ColumnSelector
                        applyFilter={ (col) => {
                            props.setImpactSummaryColumns(setImpactColumns(props.columns, col));
                        } }
                        columns={ props.columns.filter(col => col.title.length > 0)
                            .map(col => (col.hasSymbol ? fp.set('symbol', props.currencyToShow, col) : col)) }
                    />
                </div>
                <div className={ styles.separator }>&nbsp;</div>
                <ExportAsCsvButton
                    csvData={ memoizedRows }
                    csvExportEmpty={ props.csvExportEmpty }
                    csvFilename={ calculateCsvFilename(props.eodDate) }
                    csvMapping={ processDataForCsvExport }
                />
            </GridHeader>
            <div className={ styles.gridContainer }>
                {props.columns.length ? (
                    <Grid
                        columns={ memoizedColumns }
                        defaultColumnSpacing={ 2 }
                        getRowLevelClassName={ getRowLevelClassName }
                        loading={ props.loading }
                        rows={ memoizedRows }
                        styles={ gridStyles }
                        symbol={ props.currencyToShow }
                        onExpanderClick={ ({ title }) => { props.expandRows(title); } }
                    >
                        IM Impact Summary
                    </Grid>
                ) : null}
            </div>
        </div>
    );
};

ImImpactSummary.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string
        })
    ),
    initialise: PropTypes.func.isRequired,
    currencyToShow: PropTypes.string,
    eodDate: PropTypes.string,
    loading: PropTypes.bool,
    csvExportEmpty: PropTypes.func.isRequired,
    imImpactSummaryLoaded: PropTypes.func.isRequired,
    setImpactSummaryColumns: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    summaryRows: PropTypes.arrayOf(PropTypes.shape({})),
    originalTrades: PropTypes.arrayOf(PropTypes.shape({})),
    submitBookingTrades: PropTypes.func.isRequired,
    trades: PropTypes.arrayOf(PropTypes.shape({})),
    allCsas: PropTypes.arrayOf(PropTypes.shape({})),
    showAllRows: PropTypes.bool,
    setShowAllRows: PropTypes.func.isRequired,
    expandRows: PropTypes.func.isRequired,
    expandedRows: PropTypes.objectOf(PropTypes.bool),
    activeCsa: PropTypes.string,
    activeCsaReadOnly: PropTypes.string,
    readOnly: PropTypes.bool,
    setCurrencyToShow: PropTypes.func.isRequired
};

ImImpactSummary.defaultProps = {
    columns: [],
    currencyToShow: '',
    eodDate: '',
    loading: false,
    originalTrades: [],
    rows: [],
    summaryRows: [],
    trades: [],
    allCsas: [],
    expandedRows: {
        IM: true,
        Monitoring: true,
        Dummy: true,
        Selected: true
    },
    showAllRows: false,
    readOnly: false,
    activeCsa: '',
    activeCsaReadOnly: ''
};

const mapStateToProps = state => ({
    activeCsa: getActiveCsa(getFilters(state, 'ImpactAnalysis')),
    activeCsaReadOnly: getActiveCsa(getFilters(state, 'ImpactAnalysisIdSummary')),
    allCsas: getCsas(state),
    columns: combinedColumnsSelector(state),
    currencyToShow: getCurrencyToShow(state),
    eodDate: getEodDate(getFilters(state, 'ImpactAnalysis')),
    loading: getLoading(state),
    originalTrades: getOriginalTrades(state),
    rows: getRows(state),
    summaryRows: getSummaryImpactRows(state),
    trades: getTrades(state),
    showAllRows: getShowAllRows(state),
    expandedRows: getRowsToExpand(state)
});

const mapDispatchToProps = {
    csvExportEmpty,
    compareCsasRequest,
    imImpactSummaryLoaded,
    setImpactSummaryColumns,
    submitBookingTrades,
    initialise,
    setShowAllRows,
    expandRows,
    setCurrencyToShow
};

export default withRouter(connect(mapStateToProps,
    mapDispatchToProps)(ImImpactSummary));

export { ImImpactSummary as ImImpactSummaryUnconnected };
